/**
 * LeadInquiryForm — formularz zapytania dla CIEMNYCH stron publicznych
 * (Hodowla, Reproduktor). Wysyła lead do panelu Zapytania V2 przez
 * POST /api/public/leads (uniwersalny silnik leadów + pełna atrybucja).
 *
 * To jest GŁÓWNY flow pozyskania zapytania — wcześniej formularz hodowli był
 * martwy (brak onSubmit), a reproduktora robił tylko mailto, przez co leady
 * nie trafiały do panelu. Mailto pozostaje opcjonalną akcją dodatkową.
 *
 * Props:
 *  - entityType: "kennel" | "stud"
 *  - entityId:   id encji (fallback: slug — backend ogarnia oba)
 *  - entityName: nazwa do komunikatu sukcesu
 *  - accent:     "amber" (hodowla) | "gold" (reproduktor)
 *  - defaultMessage / submitLabel / mailtoEmail / testIdPrefix
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

// Atrybucja: skąd przyszedł lead (UTM + referrer + auto-detekcja źródła)
const getAttribution = () => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source') || '';
  const referrer = document.referrer || '';
  const ref = referrer.toLowerCase();
  const utm = utm_source.toLowerCase();
  let source_type = 'direct';
  if (utm.includes('google')) source_type = 'google_organic';
  else if (utm.includes('facebook') || utm.includes('instagram') || utm.includes('tiktok')) source_type = 'social';
  else if (utm.includes('chatgpt') || utm.includes('perplexity') || utm.includes('gemini') || utm.includes('claude')) source_type = 'ai_search';
  else if (ref.includes('google.')) source_type = 'google_organic';
  else if (ref.includes('facebook.') || ref.includes('instagram.') || ref.includes('tiktok.') || ref.includes('twitter.') || ref.includes('x.com')) source_type = 'social';
  else if (ref.includes('chat.openai') || ref.includes('perplexity.ai') || ref.includes('gemini.google') || ref.includes('claude.ai')) source_type = 'ai_search';
  else if (ref && !ref.includes(window.location.hostname)) source_type = 'referral';
  return {
    utm_source,
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    referrer,
    entry_page: window.location.pathname + window.location.search,
    source_type,
  };
};

const ACCENTS = {
  amber: { focus: 'focus:border-amber-500', btn: 'bg-amber-500 hover:bg-amber-600 text-black' },
  gold: { focus: 'focus:border-[#D4AF37]', btn: 'bg-gradient-to-r from-[#D4AF37] to-[#F5DEB3] text-[#0B0F1A] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]' },
};

export default function LeadInquiryForm({
  entityType = 'kennel',
  entityId,
  entityName,
  accent = 'amber',
  defaultMessage = '',
  submitLabel = 'Wyślij zapytanie',
  mailtoEmail,
  testIdPrefix = 'lead',
}) {
  const a = ACCENTS[accent] || ACCENTS.amber;
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: defaultMessage });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Podaj imię i nazwisko'); return; }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error('Podaj telefon lub e-mail, żebyśmy mogli odpowiedzieć');
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Podaj poprawny adres e-mail');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/leads`, {
        entity_type: entityType,
        entity_id: entityId,
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        // EmailStr odrzuca "", więc wysyłamy klucz tylko gdy jest niepusty
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...getAttribution(),
      });
      setSent(true);
      toast.success('Zapytanie wysłane! Trafi do hodowcy w panelu.');
    } catch (err) {
      // FastAPI 422 zwraca `detail` jako listę obiektów (ValidationError).
      // Bez tego zabezpieczenia sonner/React próbuje renderować obiekt → crash do ErrorBoundary.
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x?.msg || (typeof x === 'string' ? x : '')).filter(Boolean).join('; ')
        : (typeof d === 'string' ? d : 'Błąd wysyłania. Spróbuj ponownie.');
      toast.error(msg || 'Błąd wysyłania. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-8" data-testid={`${testIdPrefix}-success`}>
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-300" />
        </div>
        <h4 className="text-white text-lg font-bold mb-1">Dziękujemy za wiadomość!</h4>
        <p className="text-gray-300 text-sm">
          {entityName ? `${entityName} — hodowca` : 'Hodowca'} odpowie najszybciej jak to możliwe.
        </p>
      </div>
    );
  }

  const inputCls = `w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none ${a.focus} transition-colors`;

  return (
    <form className="space-y-4" onSubmit={handleSubmit} data-testid={`${testIdPrefix}-form`}>
      <div>
        <label className="block text-sm text-gray-400 mb-2">Imię i nazwisko</label>
        <input type="text" value={form.name} onChange={set('name')} className={inputCls} placeholder="Jan Kowalski" data-testid={`${testIdPrefix}-name`} />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-2">E-mail</label>
        <input type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="jan@example.com" data-testid={`${testIdPrefix}-email`} />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-2">Telefon</label>
        <input type="tel" value={form.phone} onChange={set('phone')} className={inputCls} placeholder="+48 123 456 789" data-testid={`${testIdPrefix}-phone`} />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-2">Wiadomość</label>
        <textarea rows={4} value={form.message} onChange={set('message')} className={`${inputCls} resize-none`} placeholder="Dzień dobry, interesuje mnie..." data-testid={`${testIdPrefix}-message`} />
      </div>
      <Button type="submit" disabled={loading} className={`w-full font-bold py-3 rounded-full ${a.btn}`} data-testid={`${testIdPrefix}-submit`}>
        {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
        {submitLabel}
      </Button>
      {mailtoEmail && (
        <a
          href={`mailto:${mailtoEmail}`}
          className="block text-center text-sm text-gray-400 hover:text-white transition-colors"
          data-testid={`${testIdPrefix}-mailto`}
        >
          albo napisz e-mail bezpośrednio
        </a>
      )}
    </form>
  );
}
